import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { OffersService } from '../../services/offers.service';
import { AfterViewChecked } from '@angular/core';
import { PartnerGroup } from '../../interfaces/partners.interface';
import { Offer } from '../../interfaces/offers.interface';
import { environment } from '../../../environments/environment';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ScrollPositionService } from '../../services/scroll-position.service';
import * as L from 'leaflet';
import * as turf from '@turf/turf';
import { Subscription } from 'rxjs';
import { PwaService } from '../../services/pwa.service';
import { LocationService } from '../../services/location.service';

@Component({
  selector: 'app-offer-list',
  standalone: false,
  templateUrl: './offer-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offer-list.component.scss']
})
export class OfferListComponent implements OnChanges, AfterViewInit, AfterViewChecked {
  
  @Input() group?: number | null;
  @Output() loadingComplete = new EventEmitter<void>();

  private mapInitialized = false;

  sidebarHidden: boolean = false;

  visibleOffers: Offer[] = [];

  selectedOfferId: number | null = null;

  offers: Offer[] = [];

  currentLocation?: { lat: number, lng: number };

  mapCenter = { lat: this.getCenterLat(), lng: this.getCenterLng() };

  mapView: boolean = false;

  imagesUrl = `${environment.ImagesUrl}/offer/`;

  private leafletMap: L.Map | null = null;

  satelliteView = false;

  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  selectedOffer?: Offer;

  private locationSubscription?: Subscription;
  autoLocationUpdateEnabled: boolean = true;

  isGeolocationSupported: boolean = false;

  isMobile: boolean = false;

  constructor(
    private offersService: OffersService,
    private router: Router,
    private pwaService: PwaService,
    private scrollPositionService: ScrollPositionService,
    private locationService: LocationService
  ) {
    this.isGeolocationSupported = this.locationService.isGeolocationSupported();
    this.isMobile = this.pwaService.isMobileRunning;
    this.group = Number(localStorage.getItem('offerMenuGroupId'));
    
    // Subscribe to location updates
    this.locationSubscription = this.locationService.currentLocation$.subscribe(location => {
      if (location) {
        this.currentLocation = location;
        if (this.offers.length > 0) {
          this.calculateOfferDistances();
        }
      }
    });
  }



  // Get saved location info for display purposes
  getSavedLocationInfo(): string | null {
    return this.locationService.getSavedLocationInfo();
  }

  // Get status text for auto location updates
  getAutoLocationUpdateStatus(): string {
    return this.locationService.getAutoLocationUpdateStatus();
  }

  ngOnChanges(changes: SimpleChanges): void {
     if (changes['group']) {
       // Always use the input value, don't override with localStorage on first change
        this.group = changes['group']['currentValue'];
        if (this.group !== null && this.group !== undefined) {
          this.loadOffers();
        }
     }
  }

  ngOnInit() {
  }

  updateVisibleOffers() {
    this.visibleOffers = [...this.offers.filter(o => this.isValidPosition(o))];
  }

  focusOnOffer(offer: Offer) {
    this.sidebarHidden = true;
    this.mapCenter = { lat: offer.latitude, lng: offer.longitude };
    this.focusOnMouseOverOffer(offer);
  }

  focusOnMouseOverOffer(offer: Offer) {
    this.selectedOfferId = offer.id;


    if (this.isValidPosition(offer)) {

      if (this.leafletMap) {


        const allMarkers = [
          ...((this.leafletMap as any)._offerMarkers || []),
          ...((this.leafletMap as any)._nearestMarkers || [])
        ];

        // Find the marker that corresponds to this offer
        const targetMarker = allMarkers.find((marker: L.Marker) => {
          const markerLatLng = marker.getLatLng();
          const offerLat = Number(offer.latitude);
          const offerLng = Number(offer.longitude);

          // Compare coordinates with small tolerance for floating point precision
          const latDiff = Math.abs(markerLatLng.lat - offerLat);
          const lngDiff = Math.abs(markerLatLng.lng - offerLng);

          return latDiff < 0.0001 && lngDiff < 0.0001;
        });

        if (targetMarker) {
          // Close any open popups first
          this.leafletMap.closePopup();

          // Open the popup for the target marker
          setTimeout(() => {
            targetMarker.openPopup();
          }, 200); // Small delay to ensure map has centered
        } else {
          // If marker not found, create a temporary popup
          this.showTemporaryPopup(offer);
        }
        if (this.currentLocation) {
          this.drawRoute(this.currentLocation, { lat: offer.latitude, lng: offer.longitude });
        }

      }
    }
  }


  // Method to draw route between two points
  private drawRoute(from: { lat: number, lng: number }, to: { lat: number, lng: number }) {
    if (!this.leafletMap) return;

    // Remove existing route if any
    if ((this.leafletMap as any)._routeLine) {
      this.leafletMap.removeLayer((this.leafletMap as any)._routeLine);
      (this.leafletMap as any)._routeLine = null;
    }

    // Remove existing route markers if any
    if ((this.leafletMap as any)._routeMarkers) {
      (this.leafletMap as any)._routeMarkers.forEach((marker: L.Marker) => {
        this.leafletMap!.removeLayer(marker);
      });
      (this.leafletMap as any)._routeMarkers = [];
    }

    try {
      // Use OSRM (Open Source Routing Machine) for routing
      const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson&steps=true`;

      fetch(osrmUrl)
        .then(response => response.json())
        .then(data => {
          if (data.routes && data.routes.length > 0) {
            const route = data.routes[0];
            const coordinates = route.geometry.coordinates;

            // Convert coordinates to Leaflet format [lat, lng]
            const latLngs = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

            // Create the route line
            const routeLine = L.polyline(latLngs, {
              color: '#2196F3',
              weight: 5,
              opacity: 0.8,
              dashArray: '10, 5'
            }).addTo(this.leafletMap!);

            // Store reference to remove later
            (this.leafletMap as any)._routeLine = routeLine;

            // Add start marker (current location)
            const startIcon = L.icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            });

            const startMarker = L.marker([from.lat, from.lng], { icon: startIcon })
              .addTo(this.leafletMap!)
              .bindPopup('Start - Twoja lokalizacja');

            // Add end marker (destination)
            const endIcon = L.icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            });

            const endMarker = L.marker([to.lat, to.lng], { icon: endIcon })
              .addTo(this.leafletMap!)
              .bindPopup('Cel - Miejsce docelowe');

            // Store route markers
            (this.leafletMap as any)._routeMarkers = [startMarker, endMarker];

            // Calculate and display route info
            const distance = (route.distance / 1000).toFixed(1); // Convert to km
            const duration = Math.round(route.duration / 60); // Convert to minutes

            // Create route info popup
            const routeInfoHtml = `
            <div style="text-align: center; padding: 5px;">
              <strong>Trasa</strong><br>
              <span style="color: #2196F3;">📍 ${distance} km</span><br>
              <span style="color: #FF9800;">🕒 ${duration} min</span>
            </div>
          `;

            // Add route info popup at the middle of the route
            /*
            const midPoint = latLngs[Math.floor(latLngs.length / 2)];
            const routeInfoPopup = L.popup({
              closeOnClick: false,
              autoClose: false,
              closeButton: true,
              className: 'route-info-popup'
            })
              .setLatLng(midPoint)
              .setContent(routeInfoHtml)
              .openOn(this.leafletMap!);
              */

            // Fit map to show entire route
            this.leafletMap!.fitBounds(routeLine.getBounds(), { padding: [20, 20] });

          } else {
            console.warn('No route found');
            this.drawStraightLine(from, to);
          }
        })
        .catch(error => {
          console.error('Error fetching route:', error);
          this.drawStraightLine(from, to);
        });

    } catch (error) {
      console.error('Error in drawRoute:', error);
      this.drawStraightLine(from, to);
    }
  }


  // Fallback method to draw straight line if routing fails
  private drawStraightLine(from: { lat: number, lng: number }, to: { lat: number, lng: number }) {
    if (!this.leafletMap) return;

    // Remove existing route if any
    if ((this.leafletMap as any)._routeLine) {
      this.leafletMap.removeLayer((this.leafletMap as any)._routeLine);
    }

    // Create straight line
    const straightLine = L.polyline([
      [from.lat, from.lng],
      [to.lat, to.lng]
    ], {
      color: '#FF5722',
      weight: 3,
      opacity: 0.7,
      dashArray: '15, 10'
    }).addTo(this.leafletMap!);

    // Store reference
    (this.leafletMap as any)._routeLine = straightLine;

    // Calculate straight line distance
    const fromPoint = turf.point([from.lng, from.lat]);
    const toPoint = turf.point([to.lng, to.lat]);
    const distance = (turf.distance(fromPoint, toPoint, { units: 'kilometers' })).toFixed(1);

    // Add distance info
    const midLat = (from.lat + to.lat) / 2;
    const midLng = (from.lng + to.lng) / 2;

    const distanceInfoHtml = `
    <div style="text-align: center; padding: 5px;">
      <strong>Odległość w linii prostej</strong><br>
      <span style="color: #FF5722;">📏 ${distance} km</span>
    </div>
  `;

    L.popup({
      closeOnClick: false,
      autoClose: false,
      closeButton: true
    })
      .setLatLng([midLat, midLng])
      .setContent(distanceInfoHtml)
      .openOn(this.leafletMap!);

    // Fit map to show both points
    this.leafletMap!.fitBounds([
      [from.lat, from.lng],
      [to.lat, to.lng]
    ], { padding: [50, 50] });
  }

  // Method to clear route
  clearRoute() {
    if (!this.leafletMap) return;

    // Remove route line
    if ((this.leafletMap as any)._routeLine) {
      this.leafletMap.removeLayer((this.leafletMap as any)._routeLine);
      (this.leafletMap as any)._routeLine = null;
    }

    // Remove route markers
    if ((this.leafletMap as any)._routeMarkers) {
      (this.leafletMap as any)._routeMarkers.forEach((marker: L.Marker) => {
        this.leafletMap!.removeLayer(marker);
      });
      (this.leafletMap as any)._routeMarkers = [];
    }

    // Remove route info popup if exists
    if ((this.leafletMap as any)._routeInfoPopup) {
      this.leafletMap.removeLayer((this.leafletMap as any)._routeInfoPopup);
      (this.leafletMap as any)._routeInfoPopup = null;
    }

    // Close all popups (including route info popups)
    this.leafletMap.closePopup();
  }

  toggleSidebar() {
    this.sidebarHidden = !this.sidebarHidden;

    // Optional: trigger map resize after animation completes
    setTimeout(() => {
      if (this.leafletMap) {
        this.leafletMap.invalidateSize();
      }
    }, 300);
  }

  getCenterLat(): number {
    const first = this.offers.find(o => Number.isFinite(o.latitude) && Number.isFinite(o.longitude));
    const lat = first?.latitude ?? 51.9355;
    return Number.isFinite(lat) ? lat : 51.9355; // Zielona Góra latitude
  }

  switchToNormalView() {
    this.satelliteView = false;
    if (this.leafletMap) {
      this.leafletMap.eachLayer(layer => {
        if (layer instanceof L.TileLayer) {
          this.leafletMap!.removeLayer(layer);
        }
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.leafletMap);
    }
  }

  switchToSatelliteView() {
    this.satelliteView = true;
    if (this.leafletMap) {
      this.leafletMap.eachLayer(layer => {
        if (layer instanceof L.TileLayer) {
          this.leafletMap!.removeLayer(layer);
        }
      });
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      }).addTo(this.leafletMap);
    }
  }

  calculateOfferDistances() {
    if (!this.currentLocation) return;

    const from = turf.point([this.currentLocation.lng, this.currentLocation.lat]);

    this.offers.forEach(offer => {
      if (this.isValidPosition(offer)) {
        const to = turf.point([Number(offer.longitude), Number(offer.latitude)]);
        const distance = turf.distance(from, to, { units: 'meters' });
        offer.distance = Math.round(distance);
      } else {
        offer.distance = undefined;
      }
    });

    // Sort offers by distance (closest first)
    this.offers.sort((a, b) => {
      if (!a.distance && !b.distance) return 0;
      if (!a.distance) return 1;
      if (!b.distance) return -1;
      return a.distance - b.distance;
    });

    this.updateVisibleOffers();
  }

  showCurrentLocation(enableAutoUpdate: boolean = false) {
    if (!this.leafletMap) return;

    if (!this.isGeolocationSupported) {
      alert('Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.');
      return;
    }

    this.locationService.getCurrentLocationWithErrorHandling().subscribe({
      next: (location) => {
        this.currentLocation = location;
        this.leafletMap!.setView([location.lat, location.lng], 15);
        this.calculateOfferDistances();

        // Remove previous marker if exists
        if ((this.leafletMap as any)._currentLocationMarker) {
          this.leafletMap!.removeLayer((this.leafletMap as any)._currentLocationMarker);
        }

        // Create a red marker icon
        const redIcon = L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        });

        const locationInfo = this.getSavedLocationInfo();
        const popupText = locationInfo ?
          `Twoja lokalizacja<br><small>${locationInfo}</small>` :
          'Twoja lokalizacja';

        const marker = L.marker([location.lat, location.lng], { icon: redIcon })
          .addTo(this.leafletMap!)
          .bindPopup(popupText)
          .openPopup();

        (this.leafletMap as any)._currentLocationMarker = marker;

        // Start auto updates if requested
        if (enableAutoUpdate && !this.autoLocationUpdateEnabled) {
          this.locationService.startAutoLocationUpdate();
          this.autoLocationUpdateEnabled = true;
        }
      },
      error: (error) => {
        alert(error.message);
      }
    });
  }

  // Method to use saved location without requesting new one
  useSavedLocation() {
    if (this.currentLocation && this.leafletMap) {
      this.leafletMap.setView([this.currentLocation.lat, this.currentLocation.lng], 15);

      // Remove previous marker if exists
      if ((this.leafletMap as any)._currentLocationMarker) {
        this.leafletMap!.removeLayer((this.leafletMap as any)._currentLocationMarker);
      }

      // Create a blue marker icon for saved location
      const blueIcon = L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      const locationInfo = this.getSavedLocationInfo();
      const popupText = locationInfo ?
        `Zapisana lokalizacja<br><small>${locationInfo}</small>` :
        'Zapisana lokalizacja';

      const marker = L.marker([this.currentLocation.lat, this.currentLocation.lng], { icon: blueIcon })
        .addTo(this.leafletMap!)
        .bindPopup(popupText)
        .openPopup();

      (this.leafletMap as any)._currentLocationMarker = marker;

      this.calculateOfferDistances();
    }
  }

  isValidPosition(offer: Offer): boolean {
    const lat = Number(offer.latitude);
    const lng = Number(offer.longitude);
    //return Number.isFinite(lat) && Number.isFinite(lng) ;
    return Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && (lat !== 0 || lng !== 0);
  }

  getMarkerPosition(offer: Offer): { lat: number, lng: number } {
    const lat = Number(offer.latitude);
    const lng = Number(offer.longitude);

    return {
      lat: Number.isFinite(lat) ? lat : 51.9355,
      lng: Number.isFinite(lng) ? lng : 15.5064

    };
  }

  goToOfferDetails(id: number) {
    const offer = this.offers.find(o => o.id === id);
    let titlePart = 'oferta';
    
    if (offer?.title && offer?.partner_name) {
      titlePart = (offer.partner_name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')).replace(/^-+|-+$/g, '');
    } else if (offer?.title) {
      titlePart = offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    // Use the scroll position service with clicked item ID
    const route = ['/oferta', titlePart, id.toString()];
    const currentRoute = this.router.url; // Use actual current route instead of hardcoded '/oferty'
    this.scrollPositionService.navigateWithScrollSave(route, currentRoute, id.toString());
  }

  getCenterLng(): number {
    const first = this.offers.find(o => Number.isFinite(o.latitude) && Number.isFinite(o.longitude));
    const lng = first?.longitude ?? 15.5064;
    return Number.isFinite(lng) ? lng : 15.5064; // Zielona Góra longitude
  }

  toggleChange(event: any) {
    this.mapView = event.checked;

    if (this.mapView) {

      setTimeout(() => {
        const mapDiv = document.getElementById('bottom-pointer');
        if (mapDiv) {
          mapDiv.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
      }, 500);
    }
  }

  loadOffers() {
    this.clearRoute();

    const params: any = {
      sort: 'sort',
      sort_direction: 'desc',
      limit: 2000
    };

    if (this.offersService.searchTitle) {
      params.title = this.offersService.searchTitle;
    }


    if (this.group) {
      params.category_id = this.group;
      params.category2_id = this.group;
    } else {
    }
    

    this.offersService.getOffers(params).subscribe({
      next: (res) => {
        this.offersService.searchTitle = '';
        this.offers = res.items || [];
        this.addOfferMarkers();

        if (this.mapView) {

          setTimeout(() => {
            const mapDiv = document.getElementById('bottom-pointer');
            if (mapDiv) {
              mapDiv.scrollIntoView({ behavior: 'smooth', block: 'end' });
            } else {
              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }
          }, 500);
        }

        this.updateVisibleOffers();

        // Calculate distances if we have a saved location
        if (this.currentLocation) {
          this.calculateOfferDistances();
        }

        // Emit that loading is complete
        setTimeout(() => {
          this.loadingComplete.emit();
        }, 100);

      },
      error: (error) => {
        this.offers = [];
        // Let the interceptor handle the error display, but still handle component-specific logic
        console.error('Error loading offers:', error);
        // Emit loading complete even on error
        this.loadingComplete.emit();
      }
    });
  }

  initMap() {
    const zielonaGoraCenter: [number, number] = [51.93548, 15.50643];
    this.leafletMap = L.map('map').setView(zielonaGoraCenter, 13);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.leafletMap);

    this.addOfferMarkers();

    // If we have a saved location, show it on the map
    if (this.currentLocation) {
      setTimeout(() => {
        this.useSavedLocation();
      }, 1000);
    }
  }

  private showTemporaryPopup(offer: Offer) {
    if (!this.leafletMap || !this.isValidPosition(offer)) return;

    const position: [number, number] = [Number(offer.latitude), Number(offer.longitude)];
    const thumbHtml = offer.file
      ? `<img src="${this.imagesUrl}thumb/${offer.file}" alt="${offer.title}" style="width:120px;max-height:80px;object-fit:cover;margin-bottom:6px;border-radius:4px;" />`
      : '';
    const titlePart = offer.title
      ? offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'oferta';
    const offerUrl = `/oferta/${titlePart}/${offer.id}`;
    const distanceText = offer.distance ? `<br><small style="color:#666;">${offer.distance}m od Ciebie</small>` : '';

    const popupHtml = `
    <div style="width:200px;">
      ${thumbHtml}
      <br />
      <b><a href="javascript:void(0)" data-offer-id="${offer.id}" class="offer-link" style="text-decoration:none;color:#1976d2;cursor:pointer;">${offer.title}</a></b><br />
      ${offer.partner_name || ''}
      ${distanceText}
    </div>
  `;

    // Create and show popup
    const popup = L.popup({
      closeOnClick: true,
      autoClose: true,
      closeButton: true
    })
      .setLatLng(position)
      .setContent(popupHtml)
      .openOn(this.leafletMap);

    // Add click event listener for the offer link
    setTimeout(() => {
      const popupElement = popup.getElement();
      const offerLink = popupElement?.querySelector('.offer-link');
      if (offerLink) {
        offerLink.addEventListener('click', (e) => {
          e.preventDefault();
          const offerId = offerLink.getAttribute('data-offer-id');
          if (offerId) {
            this.goToOfferDetails(Number(offerId));
          }
        });
      }
    }, 0);
  }

  // Toggle automatic location updates
  toggleAutoLocationUpdate() {
    if (!this.isGeolocationSupported) {
      alert('Geolokalizacja nie jest wspierana przez Twoją przeglądarkę.');
      return;
    }

    this.locationService.toggleAutoLocationUpdate().subscribe({
      next: (enabled) => {
        this.autoLocationUpdateEnabled = enabled;
      },
      error: (error) => {
        let errorMessage = 'Nie można włączyć automatycznych aktualizacji lokalizacji. ';

        switch (error.type) {
          case 'PERMISSION_DENIED':
            errorMessage += 'Dostęp do lokalizacji został zablokowany. Sprawdź ustawienia przeglądarki i zezwól na dostęp do lokalizacji dla tej strony.';
            break;
          case 'POSITION_UNAVAILABLE':
            errorMessage += 'Informacje o lokalizacji są niedostępne. Sprawdź czy masz włączoną lokalizację w urządzeniu.';
            break;
          case 'TIMEOUT':
            errorMessage += 'Przekroczono czas oczekiwania na pobranie lokalizacji. Spróbuj ponownie.';
            break;
          default:
            errorMessage += 'Wystąpił nieznany błąd podczas pobierania lokalizacji.';
            break;
        }

        alert(errorMessage);
        console.warn('Failed to enable auto location updates:', error.message);
      }
    });
  }





  addOfferMarkers() {
    if (!this.leafletMap) return;

    // Remove nearest markers if any
    if ((this.leafletMap as any)._nearestMarkers) {
      (this.leafletMap as any)._nearestMarkers.forEach((m: L.Marker) => this.leafletMap!.removeLayer(m));
      (this.leafletMap as any)._nearestMarkers = [];
    }

    // Remove existing markers if needed
    if ((this.leafletMap as any)._offerMarkers) {
      (this.leafletMap as any)._offerMarkers.forEach((m: L.Marker) => this.leafletMap!.removeLayer(m));
    }
    (this.leafletMap as any)._offerMarkers = [];

    // Remove previous circle if exists
    if ((this.leafletMap as any)._nearestCircle) {
      this.leafletMap!.removeLayer((this.leafletMap as any)._nearestCircle);
      (this.leafletMap as any)._nearestCircle = null;
    }




    // Define green marker icon
    const greenIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    this.offers
      .filter(o => this.isValidPosition(o))
      .forEach(offer => {
        const position: [number, number] = [Number(offer.latitude), Number(offer.longitude)];
        const thumbHtml = offer.file
          ? `<img src="${this.imagesUrl}thumb/${offer.file}" alt="${offer.title}" style="width:120px;max-height:80px;object-fit:cover;margin-bottom:6px;border-radius:4px;" />`
          : '';
        const titlePart = offer.title
          ? offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
          : 'oferta';
        const offerUrl = `/oferta/${titlePart}/${offer.id}`;
        const distanceText = offer.distance ? `<br><small style="color:#666;">${offer.distance}m od Ciebie</small>` : '';

        const popupHtml = `
        <div style="width:200px;">
          ${thumbHtml}
          <br />
          <b><a href="javascript:void(0)" data-offer-id="${offer.id}" class="offer-link" style="text-decoration:none;color:#1976d2;cursor:pointer;">${offer.title}</a></b><br />
          </div>
          ${offer.partner_name || ''}
          ${distanceText}
      `;
        const marker = L.marker(position, { icon: greenIcon })
          .addTo(this.leafletMap!)
          .bindPopup(popupHtml);

        // Add click event listener for the offer link
        marker.on('popupopen', () => {
          const popupElement = marker.getPopup()?.getElement();
          const offerLink = popupElement?.querySelector('.offer-link');
          if (offerLink) {
            offerLink.addEventListener('click', (e) => {
              e.preventDefault();
              const offerId = offerLink.getAttribute('data-offer-id');
              if (offerId) {
                this.goToOfferDetails(Number(offerId));
              }
            });
          }
        });

        (this.leafletMap as any)._offerMarkers.push(marker);
      });

    this.updateVisibleOffers();
  }

  showNearestOffers(distanceMeters: number) {
    if (!this.currentLocation) {
      alert('Najpierw wybierz swoją lokalizację.');
      return;
    }

    // Remove existing markers if needed
    if ((this.leafletMap as any)._offerMarkers) {
      (this.leafletMap as any)._offerMarkers.forEach((m: L.Marker) => this.leafletMap!.removeLayer(m));
    }
    (this.leafletMap as any)._offerMarkers = [];

    // Remove previous nearest markers
    if ((this.leafletMap as any)._nearestMarkers) {
      (this.leafletMap as any)._nearestMarkers.forEach((m: L.Marker) => this.leafletMap!.removeLayer(m));
    }
    (this.leafletMap as any)._nearestMarkers = [];

    // Remove previous circle if exists
    if ((this.leafletMap as any)._nearestCircle) {
      this.leafletMap!.removeLayer((this.leafletMap as any)._nearestCircle);
      (this.leafletMap as any)._nearestCircle = null;
    }

    const from = turf.point([this.currentLocation.lng, this.currentLocation.lat]); // [lng, lat]
    const nearestOffers = this.offers.filter(o => {
      if (!this.isValidPosition(o)) return false;
      const to = turf.point([Number(o.longitude), Number(o.latitude)]);
      const dist = turf.distance(from, to, { units: 'meters' });
      return dist <= distanceMeters;
    });

    // Add green markers for nearest offers
    const greenIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    nearestOffers.forEach(offer => {
      const position: [number, number] = [Number(offer.latitude), Number(offer.longitude)];
      const thumbHtml = offer.file
        ? `<img src="${this.imagesUrl}thumb/${offer.file}" alt="${offer.title}" style="width:120px;max-height:80px;object-fit:cover;margin-bottom:6px;border-radius:4px;" />`
        : '';
      const titlePart = offer.title
        ? offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        : 'oferta';
      const offerUrl = `/oferta/${titlePart}/${offer.id}`;
      const distanceText = offer.distance ? `<br><small style="color:#666;">${offer.distance}m od Ciebie</small>` : '';

      const popupHtml = `
      <div style="width:200px;">
        ${thumbHtml}
        <br />
        <b><a href="javascript:void(0)" data-offer-id="${offer.id}" class="offer-link" style="text-decoration:none;color:#1976d2;cursor:pointer;">${offer.title}</a></b><br />
        ${offer.partner_name || ''}
        ${distanceText}
      </div>
    `;
      const marker = L.marker(position, { icon: greenIcon })
        .addTo(this.leafletMap!)
        .bindPopup(popupHtml);

      // Add click event listener for the offer link
      marker.on('popupopen', () => {
        const popupElement = marker.getPopup()?.getElement();
        const offerLink = popupElement?.querySelector('.offer-link');
        if (offerLink) {
          offerLink.addEventListener('click', (e) => {
            e.preventDefault();
            const offerId = offerLink.getAttribute('data-offer-id');
            if (offerId) {
              this.goToOfferDetails(Number(offerId));
            }
          });
        }
      });

      (this.leafletMap as any)._nearestMarkers?.push(marker);

    });

    // Draw a circle around the current location
    const circle = L.circle([this.currentLocation.lat, this.currentLocation.lng], {
      radius: distanceMeters,
      color: '#1976d2',
      fillColor: '#1976d2',
      fillOpacity: 0.05,
      weight: 2
    }).addTo(this.leafletMap!);
    (this.leafletMap as any)._nearestCircle = circle;

    this.updateVisibleOffers();
  }

  ngAfterViewChecked(): void {
    if (this.mapView && !this.mapInitialized && document.getElementById('map')) {
      this.initMap();
      this.mapInitialized = true;
    }
    if (!this.mapView && this.mapInitialized) {
      // Optionally destroy the map instance here
      this.mapInitialized = false;
    }
  }

  ngAfterViewInit(): void {
    const state = history.state;
    if (state && typeof state.scrollY === 'number') {
      setTimeout(() => window.scrollTo(0, state.scrollY), 0);
    }
  }

  toggleView() {
    this.mapView = !this.mapView;
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    this.locationService.stopAutoLocationUpdate();
    this.locationSubscription?.unsubscribe();
  }
}